import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { UserPlus, Loader2, Users as UsersIcon, Store } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  role: "particular",
  first_name: "",
  last_name: "",
  email: "",
  city: "",
  postal_code: "",
  affiliation_name: "",
  business_name: "",
  business_phone: "",
};

export default function AdminUserCreateDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const isPro = form.role === "professional";

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error("Prénom, nom et email sont obligatoires.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", { ...form });
      toast.success(`Compte créé — e-mail d'invitation envoyé à ${data.email}`);
      setForm(EMPTY);
      setOpen(false);
      onCreated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la création.");
    } finally {
      setSaving(false);
    }
  };

  const RoleBtn = ({ value, label, Icon }) => (
    <button
      type="button"
      onClick={() => set("role", value)}
      data-testid={`admin-user-create-role-${value}`}
      className={`flex-1 flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${
        form.role === value
          ? "border-[#E05638] bg-orange-50 text-[#E05638]"
          : "border-slate-200 text-slate-600 hover:border-slate-300"
      }`}
    >
      <Icon size={16} /> {label}
    </button>
  );

  return (
    <>
      <Button onClick={() => setOpen(true)} className="btn-shine text-white" size="sm" data-testid="admin-user-create-open">
        <UserPlus size={14} className="mr-1.5" /> Créer un utilisateur
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-user-create-dialog">
          <DialogHeader>
            <DialogTitle>Créer un utilisateur</DialogTitle>
            <DialogDescription>
              Un e-mail d'invitation sera envoyé pour que la personne définisse elle-même son mot de passe.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="flex gap-2">
              <RoleBtn value="particular" label="Particulier" Icon={UsersIcon} />
              <RoleBtn value="professional" label="Professionnel" Icon={Store} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prénom *</Label>
                <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)}
                       data-testid="admin-user-create-firstname" required />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)}
                       data-testid="admin-user-create-lastname" required />
              </div>
            </div>

            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                     placeholder="personne@exemple.fr" data-testid="admin-user-create-email" required />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ville</Label>
                <Input value={form.city} onChange={(e) => set("city", e.target.value)}
                       data-testid="admin-user-create-city" />
              </div>
              <div>
                <Label>Code postal</Label>
                <Input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)}
                       data-testid="admin-user-create-postal" />
              </div>
            </div>

            {isPro ? (
              <div className="grid grid-cols-2 gap-3 rounded-lg bg-orange-50/60 border border-orange-100 p-3">
                <div>
                  <Label>Nom du commerce</Label>
                  <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)}
                         placeholder="Ex: Boulangerie Durand" data-testid="admin-user-create-business-name" />
                </div>
                <div>
                  <Label>Téléphone commerce</Label>
                  <Input value={form.business_phone} onChange={(e) => set("business_phone", e.target.value)}
                         data-testid="admin-user-create-business-phone" />
                </div>
              </div>
            ) : (
              <div>
                <Label>Rattachement (organisme)</Label>
                <Input value={form.affiliation_name} onChange={(e) => set("affiliation_name", e.target.value)}
                       placeholder="Ex: CSE Entreprise XYZ (facultatif)" data-testid="admin-user-create-affiliation" />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}
                      data-testid="admin-user-create-cancel">Annuler</Button>
              <Button type="submit" disabled={saving} className="btn-shine text-white"
                      data-testid="admin-user-create-submit">
                {saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Création...</> : "Créer et inviter"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
